import React from "react";

const formatResourceOrientation = (orientation, text) => {
  let iconClass = "";
  if (orientation === "vertical") {
    iconClass = "bi bi-vr small mx-1";
  }
  if (orientation === "horizontal") {
    iconClass = "bi bi-hr small mx-1";
  }
  if (orientation === "square") {
    iconClass = "bi bi-square small mx-1";
  }
  
  return (
    <React.Fragment>
      <span className="badge text-uppercase rounded-pill bg-secondary bg-opacity-10 text-secondary fw-normal py-1 px-2">
        {iconClass && <i className={iconClass}></i>}
        {text && orientation}
      </span>
    </React.Fragment>
  );
};

const formatResourceStatus = (status) => {
  // Handle null/undefined status
  if (!status) {
    return <small className="badge rounded-pill bg-secondary bg-opacity-10 fw-normal text-secondary">-</small>;
  }
  
  const getStatusProps = (status) => {
    switch (status) {
      case "active":
        return { className: "badge rounded-pill bg-success bg-opacity-10 fw-normal text-success", text: status };
      case "draft":
        return { className: "badge rounded-pill bg-secondary bg-opacity-10 fw-normal text-secondary", text: status };
      case "pending":
        return { className: "badge rounded-pill bg-info bg-opacity-10 fw-normal text-info", text: status };
      case "schedule":
        return { className: "badge rounded-pill bg-primary bg-opacity-10 text-primary fw-normal", text: status };
      case "missing":
        return { className: "badge rounded-pill bg-dark bg-opacity-10 text-dark fw-normal", text: status };
      case "reject":
        return { className: "badge rounded-pill bg-warning bg-opacity-10 text-warning fw-normal", text: status };
      case "deleted":
        return { className: "badge rounded-pill bg-danger bg-opacity-10 text-danger fw-normal", text: status };
      default:
        return { className: "badge rounded-pill bg-secondary bg-opacity-10 fw-normal text-secondary", text: status };
    }
  };
  
  const props = getStatusProps(status);
  return <small className={props.className}>{props.text}</small>;
};

const formatResourceStatusNoText = (status) => {
  // Handle null/undefined status
  if (!status) {
    return <small className="badge rounded-pill bg-secondary bg-opacity-10 text-secondary text-uppercase">-</small>;
  }
  
  const getStatusProps = (status) => {
    const firstChar = String(status).charAt(0);
    switch (status) {
      case "active":
        return { className: "badge rounded-pill bg-success bg-opacity-10 text-success text-uppercase", text: firstChar };
      case "draft":
        return { className: "badge rounded-pill bg-secondary bg-opacity-10 text-secondary text-uppercase", text: firstChar };
      case "pending":
        return { className: "badge rounded-pill bg-info bg-opacity-10 text-info text-uppercase", text: firstChar };
      case "reject":
        return { className: "badge rounded-pill bg-warning bg-opacity-10 text-warning text-uppercase", text: firstChar };
      case "schedule":
        return { className: "badge rounded-pill bg-primary bg-opacity-10 text-primary text-uppercase", text: firstChar };
      case "missing":
        return { className: "badge rounded-pill bg-dark bg-opacity-10 text-dark text-uppercase", text: firstChar };
      case "deleted":
        return { className: "badge rounded-pill bg-danger bg-opacity-10 text-danger text-uppercase", text: firstChar };
      default:
        return { className: "badge rounded-pill bg-secondary bg-opacity-10 text-secondary text-uppercase", text: firstChar };
    }
  };
  
  const props = getStatusProps(status);
  return <small className={props.className}>{props.text}</small>;
};

const formatFileEx = (extension, size) => {
  // Handle null/undefined extension
  if (!extension) {
    return (
      <span className="format p-3">
        <i className={`bi bi-file ${size || ''}`}></i>
      </span>
    );
  }
  
  const getIconClass = (ext) => {
    switch (ext.toLowerCase()) {
      case 'svg':
        return `bi bi-filetype-svg ${size || ''}`;
      case 'ai':
        return `bi bi-filetype-ai ${size || ''}`;
      case 'eps':
        return `bi bi-files-alt ${size || ''}`;
      default:
        return `bi bi-file ${size || ''}`;
    }
  };
  
  const iconClass = getIconClass(extension);
  return (
    <span className={`format format--${extension.toLowerCase()} p-3`}>
      <i className={iconClass}></i>
    </span>
  );
};

const formatUserStatus = (disabled) => {
  const isDisabled = disabled === true;
  return (
    <small className={`badge fw-normal ${isDisabled ? 'bg-danger bg-opacity-10 text-danger' : 'bg-success bg-opacity-10 text-success'}`}>
      {isDisabled ? 'Disabled' : 'Enabled'}
    </small>
  );
};

const formatStatus = (status) => {
  // Handle null/undefined status
  if (!status) {
    return <small className="badge bg-opacity-10 text-secondary bg-secondary fw-normal">-</small>;
  }
  
  const getStatusProps = (status) => {
    switch (status) {
      case "active":
        return { className: "badge bg-opacity-10 text-success bg-success fw-normal", text: status };
      case "draft":
        return { className: "badge bg-opacity-10 text-secondary bg-secondary fw-normal", text: status };
      case "pending":
        return { className: "badge bg-opacity-10 text-warning bg-warning fw-normal", text: status };
      case "missing":
        return { className: "badge bg-opacity-10 text-warning bg-danger fw-normal", text: status };
      case "deleted":
        return { className: "badge bg-opacity-10 text-danger bg-danger fw-normal", text: status };
      default:
        return { className: "badge bg-opacity-10 text-secondary bg-secondary fw-normal", text: status };
    }
  };
  
  const props = getStatusProps(status);
  return <small className={props.className}>{props.text}</small>;
};

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 MB";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

const formatPaymentStatus = (status) => {
  // Handle null/undefined status
  if (!status) {
    return <small className="badge bg-opacity-10 text-secondary bg-secondary fw-normal">-</small>;
  }

  const getStatusProps = (status) => {
    switch (status.toLowerCase()) {
      case "success":
        return { className: "badge bg-opacity-10 text-success bg-success fw-normal", text: status };
      case "cancel":
        return { className: "badge bg-opacity-10 text-secondary bg-secondary fw-normal", text: status };
      case "pending":
        return { className: "badge bg-opacity-10 text-warning bg-warning fw-normal", text: status };
      case "failed":
      case "error":
        return { className: "badge bg-opacity-10 text-danger bg-danger fw-normal", text: status };
      default:
        return { className: "badge bg-opacity-10 text-secondary bg-secondary fw-normal", text: status };
    }
  };

  const props = getStatusProps(status);
  return <small className={props.className}>{props.text}</small>;
};

const formatOrderStatus = (status) => {
  // Use formatStatus for consistent badge rendering
  return formatStatus(status ? status.toLowerCase() : status);
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
  }).format(value);
};

const formatHelpers = {
  formatStatus,
  formatFileEx,
  formatUserStatus,
  formatResourceStatus,
  formatResourceStatusNoText,
  formatBytes,
  formatCurrency,
  formatPaymentStatus,
  formatOrderStatus,
  formatResourceOrientation
};

export default formatHelpers;
